import React, { useState } from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt } from '@/icons';
import { runtimeEnvironment } from '../config/environment';
import { useSiteGate } from '../contexts/SiteGateContext';

/**
 * Sitewide password screen shown in lower environments (test, cert) before the app loads.
 * Not a user login — a coarse gate that keeps random visitors out.
 */
export const SiteGatePage: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { unlock } = useSiteGate();

    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!password || isSubmitting) return;
        setIsSubmitting(true);
        setError(false);
        try {
            const success = await unlock(password);
            if (!success) {
                setError(true);
                setPassword('');
            }
        } catch {
            setError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: theme.colors.background,
        color: theme.colors.text.primary,
    };

    const iconStyle = {
        fontSize: '3.5rem',
        marginBottom: '1.5rem',
        color: theme.colors.primary,
    } as React.CSSProperties;

    const titleStyle: React.CSSProperties = {
        ...themedStyles.text.heading,
        fontSize: '1.75rem',
        marginBottom: '0.5rem',
    };

    const messageStyle: React.CSSProperties = {
        ...themedStyles.text.body,
        fontSize: '1rem',
        marginBottom: '1.5rem',
        opacity: 0.8,
        maxWidth: '420px',
    };

    const inputStyle: React.CSSProperties = {
        ...themedStyles.input.default,
        width: '280px',
        textAlign: 'center',
        marginBottom: '1rem',
        ...(error ? { borderColor: theme.colors.border.error } : {}),
    };

    const buttonStyle: React.CSSProperties = {
        ...themedStyles.button.primary,
        ...(isSubmitting || !password ? themedStyles.button.disabled : {}),
    };

    const errorStyle: React.CSSProperties = {
        ...themedStyles.text.body,
        color: theme.colors.status.error,
        fontSize: '0.9rem',
        marginBottom: '1rem',
        minHeight: '1.2rem',
    };

    return (
        <div style={containerStyle}>
            <FontAwesomeIcon icon={faShieldAlt} style={iconStyle as any} />
            <h1 style={titleStyle}>Restricted environment</h1>
            <p style={messageStyle}>
                This is the {runtimeEnvironment} environment. Enter the access password to continue.
            </p>
            <form onSubmit={handleSubmit}>
                <div>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(false); }}
                        placeholder="Access password"
                        autoFocus
                        autoComplete="off"
                        disabled={isSubmitting}
                        style={inputStyle}
                        aria-label="Access password"
                    />
                </div>
                <div style={errorStyle}>{error ? 'Incorrect password. Try again.' : ''}</div>
                <button type="submit" disabled={isSubmitting || !password} style={buttonStyle}>
                    {isSubmitting ? 'Checking...' : 'Enter'}
                </button>
            </form>
        </div>
    );
};
